import React from "react";
import { getEndpoint } from "./connections";
import loader from '../img/loader.gif';

/**
 * Utility Functions
 */

/**
 * Get a datetime converted from UTC
 */
function getDate(utc, format = "long") {
    let options;
    
    switch (format) {
        case "long":
            options = {
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZoneName: "short",
                hour: "2-digit",
                minute: "2-digit"
              };
            break;
          case "medium":
              options = {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit"
                };
              break;
          case 'short':
            options = {
                year: "numeric",
                month: "2-digit",
                day: "2-digit"
              };
            break;
          case 'monthDay':
            options = {
                month: "long",
                day: "numeric"
              };
            break;
        default:
            options = {
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZoneName: "short",
                hour: "2-digit",
                minute: "2-digit"
              };
            break;
    }

    let dateObj = new Date(utc);
    return dateObj.toLocaleString("en-US", options);
}


/**
 * Calculate percentage of first number over 2nd
 */
function calcRatio(firstNum, secondNum) {
    let ratio;
    // (props.awayYC / (props.awayYC + props.homeYC) * 100).toFixed(2) + '%'
    if ( firstNum == 0 && secondNum == 0 ) {
        ratio = '50%';
    } else {
        ratio = (firstNum / (firstNum + secondNum) * 100).toFixed(2) + '%';
    }
    
    return ratio;
}

/**
 * Get an image from the API
 */
async function getAPIImage(uuid, connectionInfo) {

    // Check local storage first
    const storageKey = 'aca-img-' + uuid;
    const storedValue = sessionStorage[storageKey] || null;
    if ( storedValue ) return storedValue;

    // If there's nothing in local storage for that image, fetch it, store, and display
    const imgEndpoint = getEndpoint('image', {
        uuid: uuid,
        tenant: connectionInfo.tenant
    }, {
        organizationIdFilter: connectionInfo.organization_id
    }, connectionInfo.environment);
    
    // Get data
    const response = await fetch(imgEndpoint, {
        method: 'GET',
        headers: {
            'API_KEY': connectionInfo.api_key
        },
    })
      .then(res => res.json())
      .then(
        (result) => {
            if ( result.value && result.contentType ) {
              const image = "data:" + result.contentType + ";base64," + result.value;
              try {
                sessionStorage.setItem(storageKey, image);
              }
              catch (e) {
                  console.log("Local Storage is full");
              }
              return image;
            } else {
              return null;
            }
        },
        (error) => {
          console.log(error);
        }
      );

    return response;
}

/**
 * Get team data
 */
async function getAPITeam(teamId, connectionInfo) {

    const teamEndpoint = getEndpoint('team', {
        teamId: teamId,
        tenant: connectionInfo.tenant
    }, {
        organizationIdFilter: connectionInfo.organization_id
    }, connectionInfo.environment);
    
    // Get data
    const response = await fetch(teamEndpoint, {
        method: 'GET',
        headers: {
            'API_KEY': connectionInfo.api_key
        },
    })
      .then(res => res.json())
      .then(
        (result) => {
            return result;
        },
        (error) => {
          console.log(error);
        }
      );

    return response;
}


/**
 * Get our error message
 */
function displayErrorMessage(message = '', classes = '') {
    const classesStr = "acaError " + classes;
    const messageStr = message ? message : 'Something went wrong. Try refreshing the page.';
    return (
        <div className={classesStr}>{messageStr}</div>
    );
}


/**
 * Get our loader
 */
function displayLoader(el_classes) {
    let classList = 'acaLoader';
    if ( el_classes ) classList += ' ' + el_classes;

    return (
        <div className={classList}>
            <img src={loader} alt="" />
        </div>
    );
}

/**
 * Scroll to an element
 */
function scrollTo(el) {
    const elRight = el.offsetLeft + el.offsetWidth;
    const elLeft = el.offsetLeft;

    const elParentRight = el.parentNode.offsetLeft + el.parentNode.offsetWidth;
    const elParentLeft = el.parentNode.offsetLeft;

    // check if right side of the element is not in view
    if (elRight > elParentRight + el.parentNode.scrollLeft) {
      el.parentNode.scrollLeft = elRight - elParentRight;
    }

    // check if left side of the element is not in view
    else if (elLeft < elParentLeft + el.parentNode.scrollLeft) {
      el.parentNode.scrollLeft = elLeft - elParentLeft;
    }
  }

  /**
   * Shuffle an array
   */
  function shuffle(array) {
    let currentIndex = array.length;
  
    // While there remain elements to shuffle...
    while (currentIndex != 0) {
  
      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
  
      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }

    return array;

  }

  /**
   * Cookie Functions
   */
  function setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays*24*60*60*1000));
    let expires = "expires="+ d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
  }

  function getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for(let i = 0; i <ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  /**
   * Get the generic fallback image
   */
  const getFallbackImage = () => {
    const image = 'https://latam.analyticom.de/resources/images/noImageAvailable.png';
    return (
      <div className="teamLogo">
            <img src={image} className="teamLogo__inner" />
        </div>
    );
  }

  /**
   * Sanitize and convert a string into a URL-friendly string
   */
  const convertStringToSlug = (string) => {

    // Remove accents
    let fixedString = string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Make lowercase
    fixedString = fixedString.toLowerCase();

    // Remove extra spaces and replace with dashes
    fixedString = fixedString.split(" ");
    fixedString = fixedString.filter((str) => str).map((str) => str.trim());
    console.log(fixedString);
    fixedString = fixedString.join("-");

    return fixedString;
  }


export { calcRatio, getDate, getAPIImage, getAPITeam, displayErrorMessage, displayLoader, scrollTo, shuffle, setCookie, getCookie, getFallbackImage, convertStringToSlug };